import { onBeforeUnmount } from 'vue';
import { kioskJourney } from '@/services/telemetry/kiosk-journey';

export function usePortraitVisibility(): (
  event: Event,
  ready: boolean,
  kind: string
) => void {
  const visitId = kioskJourney.id;
  let observer: IntersectionObserver | null = null;
  const images = new Map<HTMLImageElement, string>();

  function report(image: HTMLImageElement, kind: string): void {
    if (
      document.visibilityState !== 'visible' ||
      !image.complete ||
      !image.naturalWidth
    )
      return;
    kioskJourney.once(
      'portrait_result_shown',
      { portrait_kind: kind },
      visitId
    );
    observer?.disconnect();
    images.clear();
  }

  function onVisible(): void {
    if (document.visibilityState !== 'visible') return;
    for (const [image, kind] of images) {
      const rect = image.getBoundingClientRect();
      if (
        rect.width > 0 &&
        rect.height > 0 &&
        rect.bottom > 0 &&
        rect.right > 0 &&
        rect.top < window.innerHeight &&
        rect.left < window.innerWidth
      ) {
        report(image, kind);
        return;
      }
    }
  }

  document.addEventListener('visibilitychange', onVisible);
  onBeforeUnmount(() => {
    observer?.disconnect();
    images.clear();
    document.removeEventListener('visibilitychange', onVisible);
  });

  return (event, ready, kind): void => {
    const image = event.target;
    if (
      !(visitId && ready && image instanceof HTMLImageElement) ||
      typeof IntersectionObserver === 'undefined'
    )
      return;
    images.set(image, kind);
    observer ??= new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const target = entry.target as HTMLImageElement;
        const portraitKind = images.get(target);
        if (entry.isIntersecting && portraitKind) {
          report(target, portraitKind);
          break;
        }
      }
    });
    observer.observe(image);
  };
}
